import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

// Currency symbols map - matches CurrencySelect component options
export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  INR: '₹',
  CAD: 'C$',
  AUD: 'A$',
  CHF: 'CHF',
  HKD: 'HK$',
  SGD: 'S$',
  SEK: 'kr',
  KRW: '₩',
  NOK: 'kr',
  NZD: 'NZ$',
  MXN: 'MX$',
  ZAR: 'R',
  BRL: 'R$',
  RUB: '₽',
  TRY: '₺',
  PLN: 'zł',
  THB: '฿',
  IDR: 'Rp',
  MYR: 'RM',
  PHP: '₱',
  VND: '₫',
  AED: 'د.إ',
  SAR: '﷼',
  QAR: '﷼',
  KWD: 'د.ك',
  BHD: '.د.ب',
  OMR: '﷼',
  EGP: 'E£',
  PKR: 'Rs',
  BDT: '৳',
  LKR: 'Rs',
  NPR: 'Rs',
  NGN: '₦',
  KES: 'KSh',
  GHS: '₵',
  MAD: 'د.م.',
  TND: 'د.ت',
  ILS: '₪',
  JOD: 'د.ا',
  LBP: 'ل.ل',
  DZD: 'د.ج',
  IQD: 'ع.د',
  IRR: '﷼',
  AFN: '؋',
  MMK: 'K',
  CLP: 'CLP$',
  COP: 'COL$',
  PEN: 'S/',
  ARS: 'AR$',
  VES: 'Bs.',
  UAH: '₴',
  CZK: 'Kč',
  HUF: 'Ft',
  RON: 'lei',
  BGN: 'лв',
  HRK: 'kn',
  RSD: 'дин.',
  ISK: 'kr',
  DKK: 'kr',
  TWD: 'NT$',
};

// Default currency is USD
const DEFAULT_CURRENCY = 'USD';

interface CurrencyContextType {
  currency: string;
  currencySymbol: string;
  loading: boolean;
  refreshCurrency: () => Promise<void>;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
};

// Helper function to get symbol from currency code
export const getCurrencySymbol = (currencyCode: string): string => {
  return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
};

export const CurrencyProvider = ({ children }: { children: React.ReactNode }) => {
  const [currency, setCurrency] = useState<string>(DEFAULT_CURRENCY);
  const [loading, setLoading] = useState(true);

  const fetchCurrency = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setCurrency(DEFAULT_CURRENCY);
        setLoading(false);
        return;
      }

      const { data: profile, error } = await supabase
        .from("profiles")
        .select("currency")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error && error.code !== "PGRST116") {
        console.error("Error fetching currency:", error);
        setLoading(false);
        return;
      }

      // Use profile currency if set, otherwise default to USD
      setCurrency(profile?.currency || DEFAULT_CURRENCY);
    } catch (error) {
      console.error("Error fetching currency:", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshCurrency = async () => {
    setLoading(true);
    await fetchCurrency();
  };

  useEffect(() => {
    fetchCurrency();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        fetchCurrency();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const currencySymbol = getCurrencySymbol(currency);

  return (
    <CurrencyContext.Provider value={{ currency, currencySymbol, loading, refreshCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
};
